import { useEffect, useRef, useState } from 'react';
import {
  api,
  ApiError,
  ticketAttachmentUrl,
  type Ticket,
  type TicketAttachment,
  type TicketPipeline,
  type TicketsBoard as TicketsBoardData,
} from '@/api/client';
import { fmtDate, fmtDuration } from '@/lib/format';
import { Badge, Button, Card, ErrorNote, Spinner, card, cx, input, mutedText } from '@/ui';

const STAFF_SUBMITTER_DOMAINS = new Set(['besty.ai', 'getbesty.ai']);

type TicketAttachmentsState = {
  ticketId: string;
  status: 'loading' | 'loaded' | 'error';
  attachments: TicketAttachment[];
};

function isStaffSubmitter(email: string): boolean {
  const domain = email.slice(email.lastIndexOf('@') + 1).toLowerCase();
  return STAFF_SUBMITTER_DOMAINS.has(domain);
}

function priorityTone(priority: string): 'amber' | 'blue' | 'neutral' {
  const normalized = priority.toUpperCase();
  if (normalized === 'HIGH' || normalized === 'URGENT') return 'amber';
  if (normalized === 'MEDIUM') return 'blue';
  return 'neutral';
}

function elapsedSince(iso: string | null, now: number): number | null {
  if (!iso) return null;
  const timestamp = new Date(iso).getTime();
  return Number.isNaN(timestamp) ? null : Math.max(0, now - timestamp);
}

function queueDuration(ticket: Ticket, now: number): string {
  const closedAt = ticket.closedAt ? new Date(ticket.closedAt).getTime() : NaN;
  const end = Number.isFinite(closedAt) ? closedAt : now;
  return fmtDuration(elapsedSince(ticket.createdAt, end));
}

function formatAttachmentSize(size: number | null): string | null {
  if (size === null || !Number.isFinite(size) || size < 0) return null;
  if (size >= 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.ceil(size / 1024)} KB`;
}

function TicketCard({ ticket, now, onSelect }: { ticket: Ticket; now: number; onSelect: () => void }) {
  const submitterNames = (ticket.submitters ?? []).map((submitter) => submitter.name ?? submitter.email).join(', ');
  const footerAttribution = [submitterNames || null, ticket.ownerName].filter(Boolean).join(' · ');
  const content = (
    <>
      <div className="flex items-start justify-between gap-2">
        <h3 className="min-w-0 flex-1 text-xs font-medium text-neutral-900">
          {ticket.subject?.trim() || 'Untitled ticket'}
        </h3>
        {ticket.priority && <Badge tone={priorityTone(ticket.priority)}>{ticket.priority}</Badge>}
      </div>
      {ticket.content?.trim() && (
        <p className="mt-2 line-clamp-3 text-xs leading-5 text-neutral-500">{ticket.content.trim()}</p>
      )}
      <div className="mt-3 text-[11px] text-neutral-500">In queue {queueDuration(ticket, now)}</div>
      <div className="mt-3 flex items-center justify-between gap-2 text-[11px] text-neutral-400">
        <span>{fmtDate(ticket.createdAt)}</span>
        {footerAttribution && <span className="min-w-0 truncate text-right">{footerAttribution}</span>}
      </div>
    </>
  );
  return (
    <button
      type="button"
      className={cx(card, 'block w-full p-3 text-left transition-colors hover:border-neutral-300 hover:shadow')}
      onClick={onSelect}
    >
      {content}
    </button>
  );
}

function pipelineTickets(board: TicketsBoardData, pipeline: TicketPipeline | undefined): Ticket[] {
  if (!pipeline) return board.tickets;
  return board.tickets.filter((ticket) => ticket.pipelineId === pipeline.id || ticket.pipelineId === null);
}

export function TicketsBoard() {
  const [board, setBoard] = useState<TicketsBoardData | null>(null);
  const [selectedPipelineId, setSelectedPipelineId] = useState('');
  const [selectedSubmitter, setSelectedSubmitter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [ticketAttachmentsState, setTicketAttachmentsState] = useState<TicketAttachmentsState | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);
  const selectedTicketId = selectedTicket?.id;

  async function load(refresh = false) {
    setLoading(true);
    setError(null);
    try {
      const nextBoard = await api.tickets(refresh);
      setBoard(nextBoard);
      setSelectedPipelineId((current) =>
        nextBoard.pipelines.some((pipeline) => pipeline.id === current) ? current : (nextBoard.pipelines[0]?.id ?? '')
      );
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        window.location.reload();
        return;
      }
      setError('Could not load tickets. Try again in a moment.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  useEffect(() => {
    const interval = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!board || !selectedSubmitter) return;
    const selectedPipeline = board.pipelines.find((item) => item.id === selectedPipelineId);
    const hasSubmitter = pipelineTickets(board, selectedPipeline).some((ticket) =>
      (ticket.submitters ?? []).some((submitter) => submitter.email === selectedSubmitter)
    );
    if (!hasSubmitter) setSelectedSubmitter('');
  }, [board, selectedPipelineId, selectedSubmitter]);

  useEffect(() => {
    if (!selectedTicketId) {
      setTicketAttachmentsState(null);
      return;
    }

    let cancelled = false;
    setTicketAttachmentsState({ ticketId: selectedTicketId, status: 'loading', attachments: [] });
    void api
      .ticketAttachments(selectedTicketId)
      .then(({ attachments }) => {
        if (!cancelled) {
          setTicketAttachmentsState({ ticketId: selectedTicketId, status: 'loaded', attachments });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setTicketAttachmentsState({ ticketId: selectedTicketId, status: 'error', attachments: [] });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [selectedTicketId]);

  useEffect(() => {
    if (!selectedTicket) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSelectedTicket(null);
        return;
      }
      if (event.key !== 'Tab') return;

      const panel = panelRef.current;
      if (!panel) return;
      const focusable = Array.from(
        panel.querySelectorAll<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
      );
      if (focusable.length === 0) {
        event.preventDefault();
        panel.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === panel || active === first || !panel.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === panel || active === last || !panel.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [selectedTicket]);

  useEffect(() => {
    if (!selectedTicket) return;
    previouslyFocusedRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    panelRef.current?.focus();
    return () => {
      const previouslyFocused = previouslyFocusedRef.current;
      if (previouslyFocused && document.contains(previouslyFocused)) previouslyFocused.focus();
      previouslyFocusedRef.current = null;
    };
  }, [Boolean(selectedTicket)]);

  if (loading && !board) return <Spinner label="Loading tickets" />;
  if (error && !board) {
    return (
      <Card className="p-4">
        <ErrorNote>{error}</ErrorNote>
        <Button className="mt-4" onClick={() => void load()}>
          Retry
        </Button>
      </Card>
    );
  }
  if (!board) return null;

  const pipeline = board.pipelines.find((item) => item.id === selectedPipelineId);
  const pipelineTicketList = pipelineTickets(board, pipeline);
  const submitterCounts = new Map<string, { name: string | null; count: number }>();
  for (const ticket of pipelineTicketList) {
    for (const submitter of ticket.submitters ?? []) {
      if (isStaffSubmitter(submitter.email)) continue;
      const email = submitter.email.toLowerCase();
      const current = submitterCounts.get(email);
      if (current) {
        current.count += 1;
        if (!current.name && submitter.name) current.name = submitter.name;
      } else {
        submitterCounts.set(email, { name: submitter.name, count: 1 });
      }
    }
  }
  const submitterOptions = Array.from(submitterCounts, ([email, { name, count }]) => ({
    email,
    name,
    count,
    label: name ?? email,
  })).sort(
    (a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }) || a.email.localeCompare(b.email)
  );
  const tickets = selectedSubmitter
    ? pipelineTicketList.filter((ticket) =>
        (ticket.submitters ?? []).some((submitter) => submitter.email === selectedSubmitter)
      )
    : pipelineTicketList;
  const stageIds = new Set(pipeline?.stages.map((stage) => stage.id) ?? []);
  const otherTickets = tickets.filter((ticket) => !ticket.stageId || !stageIds.has(ticket.stageId));
  const stageCounts = (pipeline?.stages ?? [])
    .map((stage) => ({
      label: stage.label,
      count: tickets.filter((ticket) => ticket.stageId === stage.id).length,
    }))
    .filter((stage) => stage.count > 0)
    .map((stage) => `${stage.label} ${stage.count}`);
  if (otherTickets.length > 0) stageCounts.push(`Other ${otherTickets.length}`);
  const stageCountsText = stageCounts.join(' · ');
  const selectedStage = selectedTicket?.stageId
    ? pipeline?.stages.find((stage) => stage.id === selectedTicket.stageId)
    : undefined;
  const selectedAttachments: TicketAttachmentsState | null = selectedTicket
    ? ticketAttachmentsState?.ticketId === selectedTicket.id
      ? ticketAttachmentsState
      : { ticketId: selectedTicket.id, status: 'loading', attachments: [] }
    : null;
  const attachmentItems = selectedAttachments?.status === 'loaded' ? selectedAttachments.attachments : [];
  const imageAttachments = attachmentItems.filter(
    (attachment) =>
      attachment.previewable &&
      !(attachment.width !== null && attachment.height !== null && attachment.width <= 64 && attachment.height <= 64)
  );
  const fileAttachments = attachmentItems.filter((attachment) => !attachment.previewable);
  const showAttachments =
    selectedAttachments?.status === 'loading' ||
    selectedAttachments?.status === 'error' ||
    imageAttachments.length > 0 ||
    fileAttachments.length > 0;

  return (
    <>
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            {board.pipelines.length > 1 ? (
              <select
                className={cx(input, 'pr-8')}
                value={selectedPipelineId}
                onChange={(event) => setSelectedPipelineId(event.target.value)}
                aria-label="Ticket pipeline"
              >
                {board.pipelines.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.label}
                  </option>
                ))}
              </select>
            ) : (
              <span className="text-sm font-semibold text-neutral-900">{pipeline?.label ?? 'Tickets'}</span>
            )}
            <select
              className={cx(input, 'pr-8')}
              value={selectedSubmitter}
              onChange={(event) => setSelectedSubmitter(event.target.value)}
              aria-label="Submitter"
            >
              <option value="">All submitters</option>
              {submitterOptions.map((option) => (
                <option key={option.email} value={option.email}>
                  {option.label} ({option.count})
                </option>
              ))}
            </select>
            <span className={mutedText}>{tickets.length} tickets</span>
            {selectedSubmitter && (
              <>
                <span className={mutedText}>{stageCountsText}</span>
                <Button variant="ghost" onClick={() => setSelectedSubmitter('')}>
                  Clear
                </Button>
              </>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <span className={cx(mutedText, 'hidden md:inline')}>
              Read-only mirror of HubSpot. Updates in HubSpot appear here.
            </span>
            <Button variant="secondary" disabled={loading} onClick={() => void load(true)}>
              {loading ? 'Refreshing' : 'Refresh'}
            </Button>
          </div>
        </div>

        {error && <ErrorNote>{error}</ErrorNote>}

        <div className="flex gap-3 overflow-x-auto pb-2">
          {(pipeline?.stages ?? []).map((stage) => {
            const stageTickets = tickets.filter((ticket) => ticket.stageId === stage.id);
            return (
              <Card key={stage.id} className="flex min-w-[260px] w-[280px] shrink-0 flex-col">
                <div className="flex items-center justify-between gap-2 border-b border-neutral-200 px-3 py-2.5">
                  <h2 className="min-w-0 truncate text-xs font-semibold text-neutral-700">{stage.label}</h2>
                  <Badge tone={stage.isClosed ? 'green' : 'neutral'}>{stageTickets.length}</Badge>
                </div>
                <div className="flex flex-1 flex-col gap-2 p-2">
                  {stageTickets.map((ticket) => (
                    <TicketCard key={ticket.id} ticket={ticket} now={now} onSelect={() => setSelectedTicket(ticket)} />
                  ))}
                </div>
              </Card>
            );
          })}
          {otherTickets.length > 0 && (
            <Card className="flex min-w-[260px] w-[280px] shrink-0 flex-col">
              <div className="flex items-center justify-between gap-2 border-b border-neutral-200 px-3 py-2.5">
                <h2 className="text-xs font-semibold text-neutral-700">Other</h2>
                <Badge tone="neutral">{otherTickets.length}</Badge>
              </div>
              <div className="flex flex-1 flex-col gap-2 p-2">
                {otherTickets.map((ticket) => (
                  <TicketCard key={ticket.id} ticket={ticket} now={now} onSelect={() => setSelectedTicket(ticket)} />
                ))}
              </div>
            </Card>
          )}
          {board.pipelines.length === 0 && (
            <Card className="p-4">
              <p className={mutedText}>No ticket pipelines found.</p>
            </Card>
          )}
        </div>
      </div>
      {selectedTicket && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setSelectedTicket(null)}
        >
          <div
            className={cx(card, 'max-h-[80vh] w-full max-w-lg overflow-y-auto p-4')}
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={selectedTicket.subject?.trim() || 'Untitled ticket'}
            tabIndex={-1}
            ref={panelRef}
          >
            <div className="flex items-start justify-between gap-3">
              <h2 className="min-w-0 flex-1 text-sm font-semibold text-neutral-900">
                {selectedTicket.subject?.trim() || 'Untitled ticket'}
              </h2>
              <Button type="button" variant="ghost" onClick={() => setSelectedTicket(null)}>
                Close
              </Button>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <Badge>{selectedStage?.label ?? 'Other'}</Badge>
              {selectedTicket.priority && (
                <Badge tone={priorityTone(selectedTicket.priority)}>{selectedTicket.priority}</Badge>
              )}
              <span className={mutedText}>{fmtDate(selectedTicket.createdAt)}</span>
            </div>
            {(selectedTicket.submitters ?? []).length > 0 && (
              <div className={cx(mutedText, 'mt-2')}>
                From{' '}
                {(selectedTicket.submitters ?? [])
                  .map((submitter) => (submitter.name ? `${submitter.name} · ${submitter.email}` : submitter.email))
                  .join(', ')}
              </div>
            )}
            <div className={cx(mutedText, 'mt-2')}>In queue {queueDuration(selectedTicket, now)}</div>
            {selectedTicket.content?.trim() ? (
              <div className="mt-4 whitespace-pre-wrap text-xs text-neutral-700">{selectedTicket.content.trim()}</div>
            ) : (
              <p className={cx(mutedText, 'mt-4')}>No description</p>
            )}
            {showAttachments && (
              <section className="mt-4 border-t border-neutral-200 pt-4">
                <h3 className="text-xs font-semibold text-neutral-700">Attachments</h3>
                {selectedAttachments?.status === 'loading' && (
                  <p className={cx(mutedText, 'mt-2')}>Loading attachments…</p>
                )}
                {selectedAttachments?.status === 'error' && (
                  <p className={cx(mutedText, 'mt-2')}>Attachments couldn't be loaded</p>
                )}
                {imageAttachments.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {imageAttachments.map((attachment) => (
                      <a
                        key={attachment.id}
                        href={ticketAttachmentUrl(selectedTicket.id, attachment.id)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <img
                          src={ticketAttachmentUrl(selectedTicket.id, attachment.id)}
                          alt={attachment.name}
                          loading="lazy"
                          className="h-24 w-24 rounded border object-cover"
                        />
                      </a>
                    ))}
                  </div>
                )}
                {fileAttachments.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {fileAttachments.map((attachment) => (
                      <a
                        key={attachment.id}
                        href={ticketAttachmentUrl(selectedTicket.id, attachment.id)}
                        download={attachment.name}
                        className="flex items-center justify-between gap-3 text-xs text-blue-700 hover:underline"
                      >
                        <span className="min-w-0 truncate">{attachment.name}</span>
                        {formatAttachmentSize(attachment.size) && (
                          <span className="shrink-0 text-neutral-500">{formatAttachmentSize(attachment.size)}</span>
                        )}
                      </a>
                    ))}
                  </div>
                )}
              </section>
            )}
          </div>
        </div>
      )}
    </>
  );
}
